import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  getLabelStyle,
  getValueStyle,
} from "@features/port-security/components/security-clearance-request-preview/UserProfileCard/UserProfileCard.styles.tsx";
import type { UserProfileCardProps } from "@features/port-security/components/security-clearance-request-preview/UserProfileCard/UserProfileCard.types.ts";
import { getDocumentsWithToken } from "@services/azureDataApi";
import { SasTokenPrefix } from "@services/user/types";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

const UserProfileCard: React.FC<UserProfileCardProps> = ({
  testId,
  imageUrl,
  fullNameEn,
  fullNameAr,
  dateOfBirth,
  passportNumber,
  passportExpiryDate,
  visaNo,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [responseImgURL, setResponseImgURL] = useState<string | undefined>(undefined);
  const [fileExtension, setFileExtension] = useState<string | undefined>(undefined);
  const { data: document, isLoading } = useQuery({
    queryKey: ["document", imageUrl],
    queryFn: () => getDocumentsWithToken(imageUrl, SasTokenPrefix.GATEPASS_SECURITY_CLEARANCE),
  });

  const isImage = !!fileExtension && /^(jpg|jpeg|png|gif|bmp)$/i.test(fileExtension);

  useEffect(() => {
    if (imageUrl) {
      // Extract file extension from imageUrl
      const match = imageUrl.match(/\.([a-zA-Z0-9]+)(?:\?|#|$)/);
      if (match) {
        setFileExtension(match[1].toLowerCase());
      } else {
        setFileExtension(undefined);
      }
    }
  }, [imageUrl]);

  useEffect(() => {
    if (document && document instanceof Blob) {
      const imgURL = URL.createObjectURL(document);
      setResponseImgURL(imgURL);
      return () => URL.revokeObjectURL(imgURL); // cleanup
    }
  }, [document]);

  const renderField = (label: string, value: string | undefined) => (
    <div className="grid min-w-0 grid-cols-[140px_minmax(0,1fr)]">
      <p className="text-sm font-normal w-[140px] shrink-0" style={getLabelStyle(theme)}>
        {label}
      </p>
      <p className="min-w-0 text-sm font-bold break-words break-all" style={getValueStyle(theme)}>
        {value}
      </p>
    </div>
  );

  const fieldPairs: [string, string | undefined, string, string | undefined][] = [
    [
      t("security-clearance.full-name-en"),
      fullNameEn,
      t("security-clearance.passport-number"),
      passportNumber,
    ],
    [
      t("security-clearance.full-name-ar"),
      fullNameAr,
      t("security-clearance.passport-expiry-date"),
      passportExpiryDate,
    ],
    [t("security-clearance.date-of-birth"), dateOfBirth, t("security-clearance.visa-no"), visaNo],
  ];

  return (
    <div data-testid={testId} className="flex rounded-md w-full">
      <div className="w-[188px] aspect-[188/140] shrink-0 rounded-xl overflow-hidden">
        {!isLoading && responseImgURL && fileExtension && isImage && (
          <img
            src={responseImgURL}
            alt={t("security-clearance.profile")}
            className="w-full h-full object-cover"
          />
        )}
      </div>

      <div className="grid min-w-0 flex-1 grid-cols-1 items-start gap-y-2 ml-6 md:grid-cols-2 md:gap-x-[100px]">
        {fieldPairs.map(([leftLabel, leftValue, rightLabel, rightValue]) => (
          <React.Fragment key={leftLabel}>
            {renderField(leftLabel, leftValue)}
            {renderField(rightLabel, rightValue)}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default UserProfileCard;
